import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';

import {
  BORDER, DRAFT_TEXT, HOVER_BG, MUTED, SUCCESS, TEXT_DARK,
  WARNING, WARNING_BG, WARNING_BODY, WARNING_HINT,
} from '../common/tokens';

// A rename that landed: the chip flushes green and settles.
const successPulse = keyframes`
  0%   { background: ${alpha(SUCCESS, 0.18)}; }
  100% { background: transparent; }
`;

// The name is taken: ring the chip that already owns it.
const duplicateFlash = keyframes`
  0%   { box-shadow: 0 0 0 4px ${alpha(WARNING, 0.35)}; }
  100% { box-shadow: 0 0 0 4px ${alpha(WARNING, 0)}; }
`;

/**
 * How the chip should paint itself, resolved from its state in priority order:
 * synthetic (a tag the user can't rename), holding (mid press-and-hold),
 * highlighted (the composer is pointing at this chip, or the tag is a
 * proposal), or plain.
 *
 * `justRenamed` puts the success pulse on any of those, except the two states
 * that bring an animation of their own.
 *
 * The brand and success colors come from the theme, so they arrive as
 * arguments rather than being read here.
 */
export default function chipAppearance({
  synthetic, isHolding, highlight, justRenamed, brandRed, successGreen,
}) {
  const base = {
    border: `1px solid ${BORDER}`,
    color: TEXT_DARK,
    background: '#fff',
    countColor: MUTED,
    animation: justRenamed ? `${successPulse} 0.9s ease forwards` : undefined,
  };

  if (synthetic) {
    return {
      ...base, border: `1px dashed ${BORDER}`, color: DRAFT_TEXT, background: HOVER_BG,
    };
  }
  if (isHolding) {
    return {
      ...base,
      border: `1px solid ${brandRed}`,
      color: brandRed,
      background: alpha(brandRed, 0.04),
      countColor: brandRed,
    };
  }
  if (highlight === 'duplicate') {
    return {
      ...base,
      border: `1px solid ${WARNING}`,
      color: WARNING_BODY,
      background: WARNING_BG,
      countColor: WARNING_HINT,
      animation: `${duplicateFlash} 1.3s ease`,
    };
  }
  if (highlight === 'proposed') {
    return {
      ...base,
      border: `1px dashed ${WARNING}`,
      color: WARNING_BODY,
      background: WARNING_BG,
      countColor: WARNING_HINT,
    };
  }
  if (highlight === 'created') {
    return {
      ...base,
      border: `1px solid ${successGreen}`,
      countColor: successGreen,
      animation: `${successPulse} 0.95s ease forwards`,
    };
  }
  return base;
}
